import { Component, OnInit } from "@angular/core";
import { MatDialog } from "@angular/material/dialog";
import { TranslateService } from "@ngx-translate/core";
import { ToastrTranslatedService } from "../shared/toastr-translated.service";
import { DomainsService } from "./domains.service";
import { Domain } from "./domains.interface";
import { MatTableDataSource } from "@angular/material/table";
import { DomainsCreateDialogComponent } from "./domains-create-dialog/domains-create-dialog.component";
import { DomainsEditDialogComponent } from "./domains-edit-dialog/domains-edit-dialog.component";
import { LimitPlanDialogComponent } from "../shared/limit-plan-dialog/limit-plan-dialog.component";
import { Router } from "@angular/router";
import { StoreExtendedService } from "../shared/store-extended.service";
import {
  ChangeAccountAction,
  CHANGE_ACCOUNT_SUCCESS,
} from "../accounts/accounts.actions";
import { AuthService } from "../auth/auth.service";
import { isFeatureBlocked } from "../shared/utils/plan-restriction.util";
import { Store } from "@ngrx/store";
import { getBillingSettingsSelector } from "../settings/settings.reducer";
import { forkJoin, of } from "rxjs";
import { catchError, take } from "rxjs/operators";
import { ResellerService } from "../reseller/reseller.service";
import {
  ResellerSubdomain,
  ResellerWithLicenses,
} from "../reseller/reseller.interface";

@Component({
  selector: "app-domains",
  templateUrl: "./domains.component.html",
  styleUrls: ["./domains.component.scss"],
  standalone: false,
})
export class DomainsComponent implements OnInit {
  public displayedColumns: string[] = [
    "expand",
    "domain",
    "googleAdminEmail",
    "createdAt",
    "actions",
  ];
  private readonly allColumns = [
    "expand",
    "domain",
    "googleAdminEmail",
    "createdAt",
    "actions",
  ];
  private readonly columnsWithoutExpand = [
    "domain",
    "googleAdminEmail",
    "createdAt",
    "actions",
  ];
  public dataSource = new MatTableDataSource<Domain>();
  public canAdd = false;
  public isReseller = false;
  public isOnMainDomain = false;
  public resellerClientsColumns = ["domain", "licenseCount"];
  public resellerClientsDataSource = new MatTableDataSource<
    ResellerSubdomain & { name?: string; photoUrl?: string | null }
  >();
  public resellerClients: (ResellerSubdomain & {
    name?: string;
    photoUrl?: string | null;
  })[] = [];
  public isLoading = true;

  constructor(
    private readonly service: DomainsService,
    private dialog: MatDialog,
    private translate: TranslateService,
    private toastr: ToastrTranslatedService,
    private store: StoreExtendedService,
    private router: Router,
    private auth: AuthService,
    private ngrxStore: Store,
    private resellerService: ResellerService,
  ) {}

  ngOnInit(): void {
    this.fetchDomains();
  }

  fetchDomains = () => {
    this.isLoading = true;
    const isResellerPlan =
      this.auth.appUser?.planId === "reseller" && !!this.auth.currentDomain;
    this.isReseller = isResellerPlan;

    forkJoin({
      domains: this.service.fetchDomains(),
      partners: isResellerPlan
        ? this.resellerService.getPartnersWithLicenses().pipe(
            catchError(() => {
              this.isReseller = false;
              return of([] as ResellerWithLicenses[]);
            }),
          )
        : of([] as ResellerWithLicenses[]),
    }).subscribe({
      next: ({ domains, partners }) => {
        this.dataSource.data = domains;
        const userDomain = domains.find(
          (d) => d.domain === this.auth.currentDomain,
        );
        this.isOnMainDomain = userDomain?.type === "main";
        this.displayedColumns = this.isOnMainDomain
          ? [...this.allColumns]
          : [...this.columnsWithoutExpand];
        this.canAdd = userDomain ? userDomain.type === "main" : false;
        if (this.isReseller) {
          const currentReseller = partners.find(
            (p) => p.domain === this.auth.currentDomain,
          );
          if (!currentReseller || currentReseller.subdomains.length === 0) {
            this.resellerClients = [];
            this.resellerClientsDataSource.data = [];
          } else {
            this.resellerClients = currentReseller.subdomains.map(
              (subdomain) => {
                const domainData = domains.find(
                  (d) => d.domain === subdomain.domain,
                );
                return {
                  ...subdomain,
                  name: domainData?.name || subdomain.domain,
                  photoUrl: domainData?.photoUrl || null,
                };
              },
            );
            this.resellerClientsDataSource.data = this.resellerClients;
          }
        }
        this.isLoading = false;
      },
      error: () => {
        this.isLoading = false;
      },
    });
  };

  editDomain = (domain: Domain) => {
    this.dialog
      .open(DomainsEditDialogComponent, {
        data: domain,
        width: "600px",
        maxWidth: "90vw",
        panelClass: "app-dialog-panel",
      })
      .afterClosed()
      .subscribe((response) => {
        if (response) {
          this.fetchDomains();
          this.toastr.success(
            this.translate.instant("domains.changeSuccess") as string,
          );
        }
      });
  };

  addDomain = () => {
    this.ngrxStore
      .select(getBillingSettingsSelector)
      .pipe(take(1))
      .subscribe((billingSettings) => {
        if (isFeatureBlocked(this.auth.isSuperAdmin, billingSettings)) {
          const planName = billingSettings?.planName?.toLowerCase() || "basic";
          this.showFreemiumRestrictionDialog(
            planName as "free" | "freemium" | "basic",
          );
          return;
        }

        this.dialog
          .open(DomainsCreateDialogComponent, {
            width: "600px",
            maxWidth: "90vw",
            panelClass: "app-dialog-panel",
          })
          .afterClosed()
          .subscribe((response) => {
            if (response) {
              this.fetchDomains();
              this.toastr.success(
                this.translate.instant("domains.addSuccess") as string,
              );
            }
          });
      });
  };

  showFreemiumRestrictionDialog = (plan: "free" | "freemium" | "basic") => {
    this.dialog.open(LimitPlanDialogComponent, {
      width: "500px",
      maxWidth: "90vw",
      data: { plan, type: "general" },
    });
  };

  goToDomain = (domain: string): void => {
    this.store.waitForAction(CHANGE_ACCOUNT_SUCCESS).subscribe(() => {
      void this.router.navigate(["signatures"]);
    });
    this.store.dispatch(new ChangeAccountAction(domain));
  };

  switchToClient = (domain: string): void => {
    this.goToDomain(domain);
  };

  isSubdomain = (element: Domain): boolean => Boolean(element.parentDomain);
}
